import { Injectable } from '@angular/core';
import { HttpHeaders, HttpParams } from '@angular/common/http';
import { Platform } from '@ionic/angular';
import { Observable, Subscription } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { CoreDataService } from '../core.data';
import { CustomEncoder } from '../http.util';
import {
  VersionConfigService,
  VersionConfig,
} from '@app/core/services/version-config';
import { API } from '@app/core/models';
import {
  ApproachingOvertimeTableDataItem,
  DataTableReport,
  DLCIFinishPostBody,
  FoodAndBeverageSummaryPayload,
  NoSchedulesEnteredTableDataItem,
  OvertimeRiskParams,
  OvertimeRiskPayload,
  OvertimeRiskRemainingTableDataItem,
  WorkflowStep,
} from './interfaces/daily-labor-check-in.interface';
import { filterTruthy } from '@app/core/utils/rxjs.util';

@Injectable({
  providedIn: 'root',
})
export class DailyLaborCheckInHttpService {
  private _sub: Array<Subscription> = [];
  private _versionBasedUrlPrefix: string;
  private _options: API.HttpOptions;

  constructor(
    private _platform: Platform,
    private _coreDataService: CoreDataService,
    private _versionConfigService: VersionConfigService,
  ) {
    this._versionBasedUrlPrefix = '';
    this._sub.push(
      this._versionConfigService
        .getVersionConfig()
        .pipe(
          filterTruthy(),
          filter((config) => {
            if (config.api?.active) {
              return true;
            }
            return false;
          }),
          map(
            (versionConfig: VersionConfig) => versionConfig.api?.prefix['dlci'],
          ),
        )
        .subscribe((prefix: string | undefined) => {
          if (prefix !== undefined) {
            this._versionBasedUrlPrefix = `${prefix}/`;
          }
        }),
    );
    this._options = this._coreDataService.getHttpOptions();
  }

  getDeptIssues(hotelId: string, date: string): Observable<any> {
    const headers = new HttpHeaders();
    const options = {
      headers: headers,
      withCredentials: true,
      params: null,
    };
    options.params = this._coreDataService.generatePlatformQueryParameters(
      new HttpParams({
        encoder: new CustomEncoder(),
      }),
      {
        entid: hotelId,
        date: date,
      },
    );
    return this._coreDataService.get(
      `${this._versionBasedUrlPrefix}dlci/dept-issues`,
      options,
    );
  }

  getWorkflowSteps(hotelId: string, date: string): Observable<WorkflowStep[]> {
    const headers = new HttpHeaders();
    const options = {
      headers,
      withCredentials: true,
      params: null,
    };
    options.params = this._coreDataService.generatePlatformQueryParameters(
      new HttpParams({
        encoder: new CustomEncoder(),
      }),
      {
        entid: hotelId,
        date,
      },
    );
    return this._coreDataService
      .get<API.Response<WorkflowStep[]>>(
        `${this._versionBasedUrlPrefix}dlci/workflow`,
        options,
      )
      .pipe(map((response) => response.payload as WorkflowStep[]));
  }

  getSummary(hotelId: string, date: string): Observable<any> {
    const headers = new HttpHeaders();
    const options = {
      headers: headers,
      withCredentials: true,
      params: null,
    };
    options.params = this._coreDataService.generatePlatformQueryParameters(
      new HttpParams({
        encoder: new CustomEncoder(),
      }),
      {
        entid: hotelId,
        date: date,
      },
    );
    return this._coreDataService.get(
      `${this._versionBasedUrlPrefix}dlci/summary`,
      options,
    );
  }

  getHousekeeping(hotelId: string, date: string): Observable<any> {
    const headers = new HttpHeaders();
    const options = {
      headers: headers,
      withCredentials: true,
      params: null,
    };
    options.params = this._coreDataService.generatePlatformQueryParameters(
      new HttpParams({
        encoder: new CustomEncoder(),
      }),
      {
        entid: hotelId,
        date: date,
      },
    );
    return this._coreDataService.get(
      `${this._versionBasedUrlPrefix}dlci/housekeeping`,
      options,
    );
  }

  postCompleted(body: DLCIFinishPostBody): Observable<any> {
    let options = {};
    if (!this._platform.is('capacitor')) {
      const headers = new HttpHeaders({
        // eslint-disable-next-line @typescript-eslint/naming-convention
        Authorization: '',
      });
      options = {
        headers: headers,
        withCredentials: true,
      };
    }
    return this._coreDataService.post(
      `${this._versionBasedUrlPrefix}dlci/completed`,
      body,
      options,
    );
  }

  getFoodAndBeverageSummary({
    date,
    entId,
    isHours,
  }: {
    date: string;
    entId: string;
    isHours: boolean;
  }): Observable<FoodAndBeverageSummaryPayload> {
    const headers = new HttpHeaders();
    const options = {
      headers: headers,
      withCredentials: true,
      params: null,
    };
    options.params = this._coreDataService.generatePlatformQueryParameters(
      new HttpParams({
        encoder: new CustomEncoder(),
      }),
      {
        date,
        entId,
        isHours,
      },
    );
    return this._coreDataService
      .get<API.Response<FoodAndBeverageSummaryPayload>>(
        `${this._versionBasedUrlPrefix}dlci/food-and-beverage-summary`,
        options,
      )
      .pipe(
        map((response) => response.payload as FoodAndBeverageSummaryPayload),
      );
  }

  getOvertimeRisk(params: OvertimeRiskParams): Observable<OvertimeRiskPayload> {
    let options = { ...this._options };
    options.params = this._coreDataService.getHttpParams(params);
    return this._coreDataService
      .get<API.Response<OvertimeRiskPayload>>(
        `${this._versionBasedUrlPrefix}dlci/overtime-risk`,
        options,
      )
      .pipe(map((response) => response.payload as OvertimeRiskPayload));
  }

  getOvertimeRiskRemaining(
    entID: string,
  ): Observable<DataTableReport<OvertimeRiskRemainingTableDataItem>> {
    let options = { ...this._options };
    options.params = this._coreDataService.getHttpParams({ entID });
    return this._coreDataService
      .get<API.Response<DataTableReport<OvertimeRiskRemainingTableDataItem>>>(
        `${this._versionBasedUrlPrefix}dlci/overtime-risk-remaining`,
        options,
      )
      .pipe(
        map(
          (response) =>
            response.payload as DataTableReport<OvertimeRiskRemainingTableDataItem>,
        ),
      );
  }

  getApproachingOvertime(
    entID: string,
  ): Observable<DataTableReport<ApproachingOvertimeTableDataItem>> {
    let options = { ...this._options };
    options.params = this._coreDataService.getHttpParams({ entID });
    return this._coreDataService
      .get<API.Response<DataTableReport<ApproachingOvertimeTableDataItem>>>(
        `${this._versionBasedUrlPrefix}dlci/approaching-overtime`,
        options,
      )
      .pipe(
        map(
          (response) =>
            response.payload as DataTableReport<ApproachingOvertimeTableDataItem>,
        ),
      );
  }

  getNoSchedulesEntered(
    entID: string,
  ): Observable<DataTableReport<NoSchedulesEnteredTableDataItem>> {
    let options = { ...this._options };
    options.params = this._coreDataService.getHttpParams({ entID });
    return this._coreDataService
      .get<API.Response<DataTableReport<NoSchedulesEnteredTableDataItem>>>(
        `${this._versionBasedUrlPrefix}dlci/no-schedules-entered`,
        options,
      )
      .pipe(
        map(
          (response) =>
            response.payload as DataTableReport<NoSchedulesEnteredTableDataItem>,
        ),
      );
  }
}
